import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronRight,
  faChevronLeft,
  faPlus,
  faSearch,
  faFilter,
  faArrowsRotate,
  faEye,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
import {
  Row,
  Col,
  Card,
  Table,
  InputGroup,
  Form,
  Button,
  Modal,
  Badge,
} from "@themesberg/react-bootstrap";
import { userCreateTicket, userGetMyTickets, getTicketById } from "../../../pages/api";
import { MEDIA_URL } from "../../helper";

const ACTIVE_BUTTON_STYLE = {
  background: "#1D4E89",
  border: "none",
  borderRadius: "0.5rem",
  color: "#ffffff",
};

const CATEGORY_OPTIONS = ["Access", "KYC", "Payment", "Technical"];
// const CATEGORY_OPTIONS = ["Payment", "Technical"];
const PRIORITY_OPTIONS = ["Low", "Medium", "High"];
const STATUS_OPTIONS = ["Open", "In Progress", "Resolved", "Closed"];

const statusBadgeClass = (status) => {
  switch (status) {
    case "Open":
      return "bg-warning text-white border border-warning-subtle";
    case "In Progress":
      return "bg-info text-white border border-info-subtle";
    case "Resolved":
      return "bg-success text-white border border-success-subtle";
    case "Closed":
      return "bg-secondary text-white border border-secondary-subtle";
    default:
      return "bg-light text-dark border border-light";
  }
};

const priorityBadgeClass = (priority) => {
  switch (priority) {
    case "Low":
      return "bg-success text-white border border-success-subtle";
    case "Medium":
      return "bg-warning text-white border border-warning-subtle";
    case "High":
      return "bg-danger text-white border border-danger-subtle";
    default:
      return "bg-light text-dark border border-light";
  }
};

const getPageItems = (currentPage, totalPages) => {
  if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
  if (currentPage <= 2) return [1, 2, 3, "...", totalPages];
  if (currentPage >= totalPages - 1) return [1, "...", totalPages - 2, totalPages - 1, totalPages];
  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
};

const getShowingRange = (pagination) => {
  const total = Number(pagination?.total || 0);
  const perPage = Number(pagination?.per_page || 10);
  const current = Math.max(1, Number(pagination?.current_page || 1));

  if (total === 0) return { start: 0, end: 0 };

  const start = (current - 1) * perPage + 1;
  const end = Math.min(current * perPage, total);
  return { start, end };
};

const emptyTicket = {
  id: null,
  ticket_id: "",
  subject: "",
  category: "",
  priority: "",
  status: "",
  attachment: "",
  created_at: "",
};


const normalizeFileUrl = (path) => {
  if (!path) return "N/A";
  const value = String(path).trim();

  if (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("blob:") ||
    value.startsWith("data:")
  ) {
    return value;
  }

  return `${MEDIA_URL}/${value.replace(/^\/+/, "")}`.replace(
    /([^:]\/)\/+/g,
    "$1"
  );
};


export default function UserSupportTicketList() {
  const [tickets, setTickets] = useState([]);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(emptyTicket);

  const [formData, setFormData] = useState({
    subject: "",
    category: "",
    priority: "",
    description: "",
    attachment: null,
  });

  const [errors, setErrors] = useState({});
  const fileRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const initialLoadRef = useRef(true);
  const skipNextEffectRef = useRef(false);

  const clearSearchDebounce = () => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
  };

  const fetchTickets = async (page = 1, overrides = {}) => {
    try {
      setLoading(true);

      const params = {
        page,
        per_page: overrides.per_page ?? pagination.per_page ?? 10,
        search: overrides.search ?? search,
        category: overrides.category ?? categoryFilter,
        priority: overrides.priority ?? priorityFilter,
        status: overrides.status ?? statusFilter,
      };

      const response = await userGetMyTickets(params);
      const apiTickets = response?.data?.data?.tickets || [];
      const apiPagination = response?.data?.data?.pagination || {};

      setTickets(apiTickets);
      setPagination({
        current_page: Number(apiPagination.current_page || page),
        last_page: Number(apiPagination.last_page || 1),
        per_page: Number(apiPagination.per_page || params.per_page || 10),
        total: Number(apiPagination.total || 0),
      });
    } catch (error) {
      console.error("Failed to fetch tickets:", error);
      toast.error("Unable to load ticket history.");
      setTickets([]);
      setPagination({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
      });
    } finally {
      setLoading(false);
    }
  };


  const fetchTicketById = async (id) => {
    setDetailLoading(true);
    try {
      const response = await getTicketById(id);
      const data = response?.data?.data || emptyTicket;

      setSelectedTicket({
        id: data?.id ?? id,
        ticket_id: data?.ticket_id || "N/A",
        subject: data?.subject || "N/A",
        category: data?.category || "N/A",
        priority: data?.priority || "N/A",
        status: data?.status || "N/A",
        admin_remark: data?.admin_remark || "N/A",
        description: data?.description || "N/A",
        // attachment: data?.attachment || "N/A",
        attachment: data?.attachment ?? null,
        created_at: data?.created_at || "N/A",
      });

      return data;
    } catch (error) {
      console.error("Failed to fetch ticket details:", error);
      toast.error("Unable to load ticket details.");
      return null;
    } finally {
      setDetailLoading(false);
    }
  };


  useEffect(() => {
    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      fetchTickets(1, {
        search: "",
        category: "",
        priority: "",
        status: "",
      });
      return;
    }

    if (skipNextEffectRef.current) {
      skipNextEffectRef.current = false;
      return;
    }

    clearSearchDebounce();
    searchDebounceRef.current = setTimeout(() => {
      fetchTickets(1, {
        search,
        category: categoryFilter,
        priority: priorityFilter,
        status: statusFilter,
      });
    }, 300);

    return () => clearSearchDebounce();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, categoryFilter, priorityFilter, statusFilter]);

  const handleClearFilters = () => {
    skipNextEffectRef.current = true;
    setSearch("");
    setCategoryFilter("");
    setPriorityFilter("");
    setStatusFilter("");
    clearSearchDebounce();

    fetchTickets(1, {
      search: "",
      category: "",
      priority: "",
      status: "",
    });
  };

  const openAddModal = () => {
    setFormData({
      subject: "",
      category: "",
      priority: "",
      description: "",
      attachment: null,
    });
    setErrors({});
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setErrors({});
    setFormData({
      subject: "",
      category: "",
      priority: "",
      description: "",
      attachment: null,
    });
    if (fileRef.current) fileRef.current.value = "";
  };

  const openViewModal = async (id) => {
    const data = await fetchTicketById(id);
    if (data) setShowViewModal(true);
  };


  const MAX_FILE_SIZE = 5 * 1024 * 1024;
  const ALLOWED_DOCUMENT_TYPES = ["application/pdf", "image/jpeg", "image/png"];

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "attachment") {
      const file = files?.[0] || null;

      if (file) {
        if (!ALLOWED_DOCUMENT_TYPES.includes(file.type)) {
          toast.error("Only JPG, PNG, and PDF files are allowed.");
          e.target.value = "";
          return;
        }

        if (file.size > MAX_FILE_SIZE) {
          toast.error("File size must be 5 MB or less.");
          e.target.value = "";
          return;
        }
      }

      setFormData((prev) => ({ ...prev, attachment: file }));
      setErrors((prev) => ({ ...prev, attachment: "" }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const trimmedSubject = formData.subject.trim();
    const trimmedDescription = formData.description.trim();

    const newErrors = {
      subject: !trimmedSubject ? "Subject is required" : "",
      category: !formData.category ? "Category is required" : "",
      priority: !formData.priority ? "Priority is required" : "",
      description: !trimmedDescription ? "Description is required" : "",
    };

    setErrors(newErrors);

    if (newErrors.subject || newErrors.category || newErrors.priority || newErrors.description) {
      toast.error("Please fill all required fields.");
      return;
    }

    try {
      setSubmitting(true);

      const payload = new FormData();
      payload.append("subject", trimmedSubject);
      payload.append("category", formData.category);
      payload.append("priority", formData.priority);
      payload.append("description", trimmedDescription);
      if (formData.attachment) payload.append("attachment", formData.attachment);

      await userCreateTicket(payload);

      toast.success("Ticket raised successfully.");
      closeAddModal();
      await fetchTickets(pagination.current_page, {
        search,
        category: categoryFilter,
        priority: priorityFilter,
        status: statusFilter,
      });
    } catch (error) {
      console.error("Ticket save failed:", error);
      toast.error("Unable to raise ticket.");
    } finally {
      setSubmitting(false);
    }
  };

  const { start, end } = getShowingRange(pagination);
  const pageItems = getPageItems(pagination.current_page, pagination.last_page);

  return (
    <>
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 py-4">
        <div>
          <h2 className="h4 mb-1">User Support</h2>
          {/* <div className="text-muted small">Raise support tickets and review your ticket history.</div> */}
        </div>

        <Button type="button" style={ACTIVE_BUTTON_STYLE} className="fw-semibold px-3 ms-auto" onClick={openAddModal}>
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          Raise Ticket
        </Button>
      </div>

      <Card className="border-0 shadow-sm rounded-4 mb-4">
        <Card.Body className="p-3 p-md-4">
          {/* <div className="d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2 mb-3">
            <div>
              <h5 className="mb-1">
                <FontAwesomeIcon icon={faFilter} className="me-2 text-muted" />
                Filters
              </h5>
              <div className="text-muted small">Search and filter your tickets.</div>
            </div>

            <Button variant="outline-secondary" onClick={handleClearFilters} className="fw-semibold">
              <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
              Reset Filters
            </Button>
          </div> */}

          <Row className="g-3">
            <Col xs={12} md={6} lg={3}>
              <Form.Label className="fw-semibold">Category</Form.Label>
              <Form.Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                style={{ boxShadow: "none" }}
              >
                <option value="">All Categories</option>
                {CATEGORY_OPTIONS.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={12} md={6} lg={3}>
              <Form.Label className="fw-semibold">Priority</Form.Label>
              <Form.Select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                style={{ boxShadow: "none" }}
              >
                <option value="">All Priorities</option>
                {PRIORITY_OPTIONS.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={12} md={6} lg={3}>
              <Form.Label className="fw-semibold">Status</Form.Label>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{ boxShadow: "none" }}
              >
                <option value="">All Status</option>
                {STATUS_OPTIONS.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={12} md={6} lg={3}>
              <Form.Label className="fw-semibold">Search</Form.Label>
              <InputGroup>
                <InputGroup.Text className="bg-white">
                  <FontAwesomeIcon icon={faSearch} className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ boxShadow: "none" }}
                />
              </InputGroup>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm rounded-4">
        <Card.Body className="p-3 p-md-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <div>
              <h5 className="mb-1">Ticket History</h5>
              {/* <div className="text-muted small">Your support tickets fetched from backend.</div> */}
            </div>
          </div>

          <div className="table-responsive">
            <Table hover className="align-middle mb-0">
              <thead>
                <tr>
                  <th>Ticket ID</th>
                  <th>Subject</th>
                  <th>Category</th>
                  <th>Priority</th>
                  <th>Status</th>
                  {/* <th>Created At</th> */}
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-5">
                      Loading tickets...
                    </td>
                  </tr>
                ) : tickets.length > 0 ? (
                  tickets.map((item) => (
                    <tr key={item.id}>
                      <td className="fw-semibold">{item.ticket_id || "N/A"}</td>
                      <td>{item.subject || "N/A"}</td>
                      <td>{item.category || "N/A"}</td>
                      <td>
                        <Badge className={`rounded-pill px-3 py-2 text-capitalize ${priorityBadgeClass(item.priority)}`}>
                          {item.priority || "N/A"}
                        </Badge>
                      </td>
                      <td>
                        <Badge className={`rounded-pill px-3 py-2 text-capitalize ${statusBadgeClass(item.status)}`}>
                          {item.status || "N/A"}
                        </Badge>
                      </td>
                      {/* <td>{item.created_at || "N/A"}</td> */}
                      <td className="text-center">
                        <Button
                          type="button"
                          variant="outline-primary"
                          size="sm"
                          onClick={() => openViewModal(item.id)}
                        >
                          <FontAwesomeIcon icon={faEye} />
                          {/* View */}
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-5">
                      No tickets found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
            <div className="text-muted small">
              Showing {start} to {end} of {pagination.total} entries
            </div>

            <div className="d-flex align-items-center gap-2 flex-wrap">
              <Button
                type="button"
                variant="outline-primary"
                disabled={pagination.current_page <= 1 || loading}
                onClick={() => {
                  clearSearchDebounce();
                  fetchTickets(Math.max(1, pagination.current_page - 1), {
                    search,
                    category: categoryFilter,
                    priority: priorityFilter,
                    status: statusFilter,
                  });
                }}
                className="course-page-btn"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </Button>

              {pageItems.map((item, index) =>
                item === "..." ? (
                  <span key={`dots-${index}`} className="text-muted px-1">
                    ...
                  </span>
                ) : (
                  <Button
                    key={item}
                    type="button"
                    variant="outline-primary"
                    disabled={loading}
                    onClick={() => {
                      clearSearchDebounce();
                      fetchTickets(item, {
                        search,
                        category: categoryFilter,
                        priority: priorityFilter,
                        status: statusFilter,
                      });
                    }}
                    className={`course-page-btn ${item === pagination.current_page ? "course-page-btn-active" : ""}`}
                  >
                    {item}
                  </Button>
                )
              )}

              <Button
                type="button"
                variant="outline-primary"
                disabled={pagination.current_page >= pagination.last_page || loading}
                onClick={() => {
                  clearSearchDebounce();
                  fetchTickets(Math.min(pagination.last_page, pagination.current_page + 1), {
                    search,
                    category: categoryFilter,
                    priority: priorityFilter,
                    status: statusFilter,
                  });
                }}
                className="course-page-btn"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Modal show={showAddModal} onHide={closeAddModal} centered size="lg">
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Raise Ticket</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <Row className="g-3">
              <Col xs={12} md={6}>
                <Form.Label className="fw-semibold">
                  Subject <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  name="subject"
                  type="text"
                  placeholder="Enter ticket subject"
                  value={formData.subject}
                  onChange={handleChange}
                  isInvalid={!!errors.subject}
                  style={{ boxShadow: "none" }}
                />
                <Form.Control.Feedback type="invalid">{errors.subject}</Form.Control.Feedback>
              </Col>

              <Col xs={12} md={6}>
                <Form.Label className="fw-semibold">
                  Category <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  isInvalid={!!errors.category}
                  style={{ boxShadow: "none" }}
                >
                  <option value="">Select category</option>
                  {CATEGORY_OPTIONS.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">{errors.category}</Form.Control.Feedback>
              </Col>

              <Col xs={12} md={6}>
                <Form.Label className="fw-semibold">
                  Priority <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  isInvalid={!!errors.priority}
                  style={{ boxShadow: "none" }}
                >
                  <option value="">Select priority</option>
                  {PRIORITY_OPTIONS.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">{errors.priority}</Form.Control.Feedback>
              </Col>

              {/* <Col xs={12} md={6}>
                <Form.Label className="fw-semibold">Attachment</Form.Label>
                <Form.Control
                  ref={fileRef}
                  name="attachment"
                  type="file"
                  accept=".png,.jpg,.jpeg,.pdf,.doc,.docx"
                  onChange={handleChange}
                  style={{ boxShadow: "none" }}
                />
              </Col> */}

              <Col xs={12} md={6}>
                <Form.Label className="fw-semibold">Attachment</Form.Label>
                <Form.Control
                  ref={fileRef}
                  name="attachment"
                  type="file"
                  accept=".png,.jpg,.jpeg,.pdf"
                  onChange={handleChange}
                  style={{ boxShadow: "none" }}
                />
              </Col>

              <Col xs={12}>
                <Form.Label className="fw-semibold">
                  Description <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  name="description"
                  placeholder="Describe your issue in detail"
                  value={formData.description}
                  onChange={handleChange}
                  isInvalid={!!errors.description}
                  style={{ boxShadow: "none" }}
                />
                <Form.Control.Feedback type="invalid">{errors.description}</Form.Control.Feedback>
              </Col>
            </Row>
          </Modal.Body>

          <Modal.Footer>
            <Button onClick={closeAddModal} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" style={ACTIVE_BUTTON_STYLE} disabled={submitting}>
              {submitting ? "Submitting..." : "Submit Ticket"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Ticket Details</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Row className="g-3">
            <Col xs={12} md={6}>
              <Card className="border-1 shadow-sm rounded-4 h-100">
                <Card.Body>
                  <div className="text-muted small">Ticket ID</div>
                  <div className="fw-semibold">{selectedTicket.ticket_id}</div>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} md={6}>
              <Card className="border-1 shadow-sm rounded-4 h-100">
                <Card.Body>
                  <div className="text-muted small">Subject</div>
                  <div className="fw-semibold">{selectedTicket.subject}</div>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} md={6}>
              <Card className="border-1 shadow-sm rounded-4 h-100">
                <Card.Body>
                  <div className="text-muted small">Category</div>
                  <div className="fw-semibold">{selectedTicket.category}</div>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} md={6}>
              <Card className="border-1 shadow-sm rounded-4 h-100">
                <Card.Body>
                  <div className="text-muted small">Priority</div>
                  <div>
                    <Badge className={`rounded-pill px-3 py-2 text-capitalize ${priorityBadgeClass(selectedTicket.priority)}`}>
                      {selectedTicket.priority}
                    </Badge>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} md={6}>
              <Card className="border-1 shadow-sm rounded-4 h-100">
                <Card.Body>
                  <div className="text-muted small">Status</div>
                  <div>
                    <Badge className={`rounded-pill px-3 py-2 text-capitalize ${statusBadgeClass(selectedTicket.status)}`}>
                      {selectedTicket.status}
                    </Badge>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} md={6}>
              <Card className="border-1 shadow-sm rounded-4 h-100">
                <Card.Body>
                  <div className="text-muted small">Created At</div>
                  <div className="fw-semibold">{selectedTicket.created_at}</div>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12}>
              <Card className="border-1 shadow-sm rounded-4 h-100">
                <Card.Body>
                  <div className="text-muted small">Description</div>
                  <div className="fw-semibold">{selectedTicket.description}</div>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12}>
              <Card className="border-1 shadow-sm rounded-4 h-100">
                <Card.Body>
                  <div className="text-muted small">Admin Remark</div>
                  <div className="fw-semibold">{selectedTicket.admin_remark}</div>
                </Card.Body>
              </Card>
            </Col>

 {selectedTicket?.attachment?.trim() && (
            <Col xs={12}>
              <Card className="border-1 shadow-sm rounded-4 h-100">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="text-muted small">Attachment</div>
                    <div className="mt-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline-primary"
                        className="text-decoration-none"
                        onClick={() =>
                          window.open(
                            normalizeFileUrl(selectedTicket.attachment),
                            "_blank",
                            "noopener,noreferrer"
                          )
                        }
                      >
                        View Document
                      </Button>
                    </div>
                    {/* <div className="text-break">{selectedTicket.attachment || "N/A"}</div> */}
                  </div>
                </Card.Body>
              </Card>
            </Col>
             )}
          </Row>
        </Modal.Body>

        {/* <Modal.Footer>
          <Button onClick={() => setShowViewModal(false)}>
            Close
          </Button>
        </Modal.Footer> */}
      </Modal>
    </>
  );
}